import { stateLabel, kindLabel } from './display-copy';
import { routeGroups, type MenuAudience } from './route-catalog';
import type { OemRecord, OemWorkspace } from '../../lib/oem-client';
import { displayValue } from '../../lib/oem-display';
export type WorkSort = 'newest' | 'oldest' | 'label' | 'version';
export interface WorkFilters {
  query: string;
  sku: string;
  state: string;
  sort: WorkSort;
}
export function workState(record: OemRecord): string {
  return displayValue(record.content.status ?? record.state);
}
export function matchingWork(
  records: OemRecord[],
  skus: OemWorkspace['skus'],
  filters: WorkFilters,
): OemRecord[] {
  const term = filters.query.trim().toLocaleLowerCase();
  const skuLabels = new Map(skus.map((s) => [s.id, s.label]));
  return records
    .filter(
      (r) =>
        (!filters.sku ||
          (filters.sku === 'project' ? r.skuId === null : r.skuId === filters.sku)) &&
        (!filters.state || workState(r) === filters.state) &&
        `${displayValue(r.content.label)} ${r.kind} ${kindLabel(r.kind)} ${workState(r)} ${stateLabel(workState(r))} ${displayValue(r.content.summary)} ${skuLabels.get(r.skuId ?? '') ?? ''} v${r.version}`
          .toLocaleLowerCase()
          .includes(term),
    )
    .sort((a, b) => {
      const order =
        filters.sort === 'label'
          ? displayValue(a.content.label ?? a.kind).localeCompare(
              displayValue(b.content.label ?? b.kind),
              'th',
            )
          : filters.sort === 'version'
            ? b.version - a.version
            : filters.sort === 'oldest'
              ? a.createdAt.localeCompare(b.createdAt)
              : b.createdAt.localeCompare(a.createdAt);
      return order || a.id.localeCompare(b.id);
    });
}
export function workDestination(
  record: OemRecord,
  formPages: Record<string, string>,
  audience: MenuAudience = 'staff',
): string | undefined {
  const permitted = new Set(routeGroups(audience).flatMap((g) => g.pages.map((p) => p.key)));
  const page =
    record.kind === 'sample'
      ? 'samples'
      : record.kind === 'artwork'
        ? 'artwork'
        : record.kind === 'hold'
          ? 'reviews'
          : formPages[record.kind];
  if (!page || !permitted.has(page)) return undefined;
  if (record.kind === 'sample') return `samples/${encodeURIComponent(record.id)}`;
  if (record.kind === 'artwork') return `artwork/${encodeURIComponent(record.id)}`;
  if (record.kind === 'hold') return 'reviews';
  return formPages[record.kind];
}
export function workPage(records: OemRecord[], page: number, size = 12) {
  const pages = Math.max(1, Math.ceil(records.length / size));
  const current = Math.min(Math.max(1, page), pages);
  return {
    records: records.slice((current - 1) * size, current * size),
    current,
    pages,
    total: records.length,
  };
}
